<template>
    <div class="flex gap-1 items-center flex-wrap gap-2">
        <div ref="tagsContainer" class="flex flex-wrap gap-2 items-center">
            <span v-for="tag in displayedTags" 
                :key="getTagName(tag)" 
                class="px-2 py-1 rounded-lg flex items-center justify-center font-medium text-sm"
                :class="{ 'cursor-move tag-item': editable }"
                :style="getTagStyle(tag)">{{ getTagName(tag) }}</span>
        </div>
        <span v-if="!editable && filteredTags.length > 5" 
            class="px-2 py-1 rounded-lg bg-gray-100 text-gray-800 flex items-center justify-center text-sm"
            :title="filteredTags.slice(5).map(t => getTagName(t)).join('\n')">+{{ filteredTags.length - 5 }}</span>
        <button v-if="props.visibleAddButton" 
            @click="createTag" 
            class="flex items-center justify-center w-6 h-6 text-green-500 cursor-pointer hover:text-green-600">
            <PlusCircleIcon class="w-6 h-6" /></button>
    </div>
</template>

<script setup>
import { defineProps, defineEmits, computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { appEvents } from '@/store/app.events';
import { PlusCircleIcon } from '@heroicons/vue/24/outline';
import { generateTagColors, getLocalizedTagName } from '@/service/tagColor.js';
import Sortable from 'sortablejs';
const emit = defineEmits(['createTag', 'update:tags']);
const { locale } = useI18n();

const props = defineProps({
    element: {
        type: Object,
        default: null
    },
    tags: {
        type: Array,
        required: true
    },
    visibleAddButton: {
        type: Boolean,
        default: true
    },
    editable: {
        type: Boolean,
        default: false
    }
});

const tagsContainer = ref(null);

onMounted(() => {
    if (props.editable && tagsContainer.value) {
        Sortable.create(tagsContainer.value, {
            animation: 150,
            draggable: '.tag-item',
            onEnd: (evt) => {
                const newTags = [...props.tags];
                const draggedItem = displayedTags.value[evt.oldIndex];
                const oldTagIndex = newTags.findIndex(t => getTagName(t) === getTagName(draggedItem));
                if (oldTagIndex > -1) {
                    const item = newTags.splice(oldTagIndex, 1)[0];
                    // Determine new position relative to newTags array
                    newTags.splice(evt.newIndex, 0, item);
                    emit('update:tags', newTags);
                }
            }
        });
    }
});

const getTagName = (tag) => {
    return getLocalizedTagName(tag, locale.value);
};

const filteredTags = computed(() => {
    return props.tags.filter(tag => {
        const name = getTagName(tag);
        return name && name.toLowerCase() !== 'unknown';
    });
});

const displayedTags = computed(() => {
    return props.editable ? filteredTags.value : filteredTags.value.slice(0, 5);
});

const getTagStyle = (tag) => {
    const name = getTagName(tag);
    if (typeof tag === 'object' && tag !== null) {
        const bg = tag.color_background;
        const fg = tag.color_font;
        if (bg && fg) {
            return { backgroundColor: bg, color: fg };
        }
    }
    if (name) {
        const c = generateTagColors(name);
        return { backgroundColor: c.color_background, color: c.color_font };
    }
    return { backgroundColor: '#f3f4f6', color: '#374151' };
};

const createTag = () => {
    emit('createTag');
    if (props.element) {
        appEvents.emit('tagsviewer.createTag', props.element);
    }
}
</script>